// src/pages/hr/EmployeeServicesModule.jsx
// ============================================================
// Enterprise Role-Based Employee Services Command Center
// Roles: Employee -> Team Lead -> HR -> Admin -> HR -> Employee
// Complete Workflow with Green Monochrome Theme & Outline Icons
// ============================================================

import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { db, storage } from "../../firebase/config";
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
  getDocs,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { toast } from "react-hot-toast";
import {
  CalendarDays,
  ClipboardList,
  Megaphone,
  Search,
  Download,
  Sparkles,
  PlusCircle,
  LifeBuoy,
  Award,
  Clock,
  MapPin,
  Users,
  Send,
  Plus,
  CheckCircle2,
  FileText,
  Eye,
  Check,
  X,
  CornerUpRight,
  ShieldCheck,
  Wrench,
  HelpCircle,
  Layers,
  ThumbsUp,
  Printer,
  ChevronRight,
  UserCheck,
} from "lucide-react";
import {
  TICKET_CATEGORIES,
  TICKET_STATUSES,
  TICKET_PRIORITIES,
  ANNOUNCEMENT_AUDIENCES,
  RECOGNITION_CATEGORIES,
  EMPLOYEE_SERVICE_SECTIONS,
  getUserRoleLabel,
  canManageAnnouncements,
  canManageEvents,
  canEscalateToHR,
  canEscalateToAdmin,
  canReturnToHR,
  canResolveTicket,
  getStatusBadgeStyle,
  getPriorityBadgeStyle,
} from "../../utils/employeeServices";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const initialForm = {
  title: "",
  description: "",
  department: "General",
  priority: "Medium",
  audience: "Entire Organization",
  expiryDate: "",
  attachmentUrl: "",
};

const initialTicket = {
  subject: "",
  category: "Payroll",
  priority: "Medium",
  description: "",
};

const initialEvent = {
  name: "",
  date: "",
  time: "10:00",
  venue: "Main Conference Hall",
  department: "All Departments",
  targetAudience: "Entire Organization",
  description: "",
  registrationRequired: true,
};

const initialRecognition = {
  employeeId: "",
  employeeName: "",
  category: "Excellence",
  message: "",
};

export default function EmployeeServicesModule() {
  const { userProfile } = useAuth();
  const role = (userProfile?.role || "employee").toLowerCase();
  const userId = userProfile?.uid || userProfile?.id || "";
  const userName = userProfile?.displayName || userProfile?.name || "User";
  const userDept = userProfile?.department || "General";
  const userTeam = userProfile?.team || "General";

  const [activeSection, setActiveSection] = useState("overview");

  // Firestore Collections State
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [recognitions, setRecognitions] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [myEventsOnly, setMyEventsOnly] = useState(false);

  // Modals & Drawers
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showEventAttendeesModal, setShowEventAttendeesModal] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showRecognitionForm, setShowRecognitionForm] = useState(false);

  // Forms State
  const [form, setForm] = useState(initialForm);
  const [ticketForm, setTicketForm] = useState(initialTicket);
  const [ticketFile, setTicketFile] = useState(null);
  const [eventForm, setEventForm] = useState(initialEvent);
  const [recognitionForm, setRecognitionForm] = useState(initialRecognition);

  const [updating, setUpdating] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [actionNotes, setActionNotes] = useState("");
  const [selectedActionModal, setSelectedActionModal] = useState(null); // 'escalate_hr' | 'escalate_admin' | 'resolve' | 'return_hr' | 'reject' | 'request_info'

  // Permission Bools
  const canManageAnn = canManageAnnouncements(role);
  const canManageEvt = canManageEvents(role);
  const isLead = role === "lead";
  const isAdmin = role === "admin";
  const isHR = role === "hr";
  const isEmployee = role === "employee";

  // Dispatch In-App Notification Helper
  const sendNotification = async ({ recipientId, title, message, type = "ticket", link = "/employee/services" }) => {
    if (!recipientId) return;
    try {
      await addDoc(collection(db, "notifications"), {
        recipientId,
        title,
        message,
        type,
        link,
        read: false,
        createdAt: serverTimestamp(),
      });
    } catch (e) {
      console.warn("Notification dispatch failed:", e);
    }
  };

  // Fetch all employees for dropdowns and lead matching
  useEffect(() => {
    if (!userId) return;
    const fetchEmployees = async () => {
      try {
        const snap = await getDocs(query(collection(db, "users")));
        setEmployees(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Failed to fetch employees:", err);
      }
    };
    fetchEmployees();
  }, [userId]);

  // Real-time Firestore Subscriptions
  useEffect(() => {
    const unsubAnnouncements = onSnapshot(
      query(collection(db, "employee_services_announcements"), orderBy("createdAt", "desc")),
      (snap) => setAnnouncements(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubEvents = onSnapshot(
      query(collection(db, "employee_services_events"), orderBy("date", "asc")),
      (snap) => setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubTickets = onSnapshot(
      query(collection(db, "employee_services_tickets"), orderBy("createdAt", "desc")),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTickets(list);
      }
    );

    const unsubRecognitions = onSnapshot(
      query(collection(db, "employee_services_recognitions"), orderBy("awardedAt", "desc")),
      (snap) => setRecognitions(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubRsvps = onSnapshot(
      collection(db, "employee_services_event_rsvps"),
      (snap) => setRsvps(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    return () => {
      unsubAnnouncements();
      unsubEvents();
      unsubTickets();
      unsubRecognitions();
      unsubRsvps();
    };
  }, []);

  // Sync selected ticket when real-time updates come in
  useEffect(() => {
    if (selectedTicket) {
      const refreshed = tickets.find((t) => t.id === selectedTicket.id);
      if (refreshed) setSelectedTicket(refreshed);
    }
  }, [tickets, selectedTicket]);

  // ── Role-Based Ticket Visibility ──────────────────────────────
  const roleFilteredTickets = useMemo(() => {
    return tickets.filter((t) => {
      if (isAdmin) {
        return true;
      }
      if (isHR) {
        return true;
      }
      if (isLead) {
        const isAuthor = t.createdById === userId;
        const isTeamMatch = (t.team && t.team === userTeam) || (t.department && t.department === userDept);
        const isAssigned = t.teamLeadId === userId || t.assignedTo === userId;
        return isAuthor || isTeamMatch || isAssigned;
      }
      // Employee: strictly sees only their own tickets
      return t.createdById === userId || t.employeeId === userId;
    });
  }, [tickets, isAdmin, isHR, isLead, userId, userTeam, userDept]);

  // Filtered Tickets by Search / Filters
  const filteredTickets = useMemo(() => {
    const q = search.toLowerCase();
    return roleFilteredTickets.filter((item) => {
      const mQ =
        !q ||
        (item.ticketNumber || "").toLowerCase().includes(q) ||
        (item.subject || "").toLowerCase().includes(q) ||
        (item.createdByName || "").toLowerCase().includes(q) ||
        (item.category || "").toLowerCase().includes(q);
      const mS = statusFilter === "all" || item.status === statusFilter;
      const mC = categoryFilter === "all" || item.category === categoryFilter;
      const mP = priorityFilter === "all" || item.priority === priorityFilter;
      return mQ && mS && mC && mP;
    });
  }, [roleFilteredTickets, search, statusFilter, categoryFilter, priorityFilter]);

  // Role-Based Announcements (Filtered by Audience)
  const filteredAnnouncements = useMemo(() => {
    const q = search.toLowerCase();
    return announcements.filter((item) => {
      if (isAdmin || isHR) {
        const matchQ = !q || (item.title || "").toLowerCase().includes(q) || (item.description || "").toLowerCase().includes(q);
        const matchCat = categoryFilter === "all" || item.department === categoryFilter;
        return matchQ && matchCat;
      }
      const aud = item.audience || "Entire Organization";
      const isMatch =
        aud === "Entire Organization" ||
        (aud === "Department" && item.department === userDept) ||
        (aud === "Team" && item.team === userTeam) ||
        item.targetEmployeeIds?.includes(userId);

      const matchQ = !q || (item.title || "").toLowerCase().includes(q) || (item.description || "").toLowerCase().includes(q);
      return isMatch && matchQ;
    });
  }, [announcements, search, categoryFilter, isAdmin, isHR, userDept, userTeam, userId]);

  // Events (with My Events toggle)
  const filteredEvents = useMemo(() => {
    const q = search.toLowerCase();
    return events.filter((item) => {
      const matchQ = !q || (item.name || "").toLowerCase().includes(q) || (item.venue || "").toLowerCase().includes(q);
      const matchCat = categoryFilter === "all" || item.department === categoryFilter;
      if (myEventsOnly) {
        const hasRsvped = rsvps.some((r) => r.eventId === item.id && r.userId === userId && r.status === "Going");
        return matchQ && matchCat && hasRsvped;
      }
      return matchQ && matchCat;
    });
  }, [events, search, categoryFilter, myEventsOnly, rsvps, userId]);

  // Recognitions
  const filteredRecognitions = useMemo(() => {
    return recognitions.filter((r) => {
      return r.status === "published" || isAdmin || isHR || r.employeeId === userId;
    });
  }, [recognitions, isAdmin, isHR, userId]);

  // ── Role-Based KPI Metrics ──────────────────────────────────
  const kpiData = useMemo(() => {
    if (isEmployee) {
      const myTickets = tickets.filter((t) => t.createdById === userId);
      const myOpen = myTickets.filter((t) => t.status !== "Resolved" && t.status !== "Closed" && t.status !== "Rejected").length;
      const myPending = myTickets.filter((t) => t.status === "Under Review" || t.status === "In Progress" || t.status === "Escalated to HR").length;
      const myRecs = recognitions.filter((r) => r.employeeId === userId).length;
      return [
        { label: "My Open Tickets", val: myOpen, Icon: ClipboardList },
        { label: "My Pending Requests", val: myPending, Icon: Clock },
        { label: "Company Announcements", val: filteredAnnouncements.length, Icon: Megaphone },
        { label: "My Recognitions", val: myRecs, Icon: Award },
      ];
    }
    if (isLead) {
      const teamTks = roleFilteredTickets;
      const teamOpen = teamTks.filter((t) => t.status === "Open" || t.status === "Assigned").length;
      const teamPending = teamTks.filter((t) => t.status === "Under Review" || t.status === "In Progress" || t.status === "Waiting for Employee").length;
      const teamRecs = recognitions.filter((r) => r.team === userTeam || r.department === userDept).length;
      return [
        { label: "Team Open Tickets", val: teamOpen, Icon: ClipboardList },
        { label: "Pending Team Requests", val: teamPending, Icon: Clock },
        { label: "Team Announcements", val: filteredAnnouncements.length, Icon: Megaphone },
        { label: "Team Recognitions", val: teamRecs, Icon: Award },
      ];
    }
    if (isHR) {
      const activeTks = tickets.filter((t) => t.status !== "Closed" && t.status !== "Resolved" && t.status !== "Rejected").length;
      const escalatedHR = tickets.filter((t) => t.status === "Escalated to HR").length;
      return [
        { label: "Active Help Desk Tickets", val: activeTks, Icon: ClipboardList },
        { label: "Escalated to HR", val: escalatedHR, Icon: Clock },
        { label: "Active Announcements", val: announcements.length, Icon: Megaphone },
        { label: "Employee Recognitions", val: recognitions.length, Icon: Award },
      ];
    }
    // Admin
    const waitingAdmin = tickets.filter((t) => t.status === "Waiting for Admin" || t.escalationLevel === "admin").length;
    const systemTickets = tickets.filter((t) => t.category === "IT/System").length;
    return [
      { label: "Escalated Requests", val: waitingAdmin, Icon: ShieldCheck },
      { label: "System-Level Tickets", val: systemTickets, Icon: Wrench },
      { label: "Active Announcements", val: announcements.length, Icon: Megaphone },
      { label: "Employee Recognitions", val: recognitions.length, Icon: Award },
    ];
  }, [isEmployee, isLead, isHR, tickets, userId, userTeam, userDept, filteredAnnouncements.length, recognitions, roleFilteredTickets, announcements.length]);

  // ── Ticket Form Submit (Employee & All Roles) ─────────────────
  const handleRaiseTicket = async (e) => {
    e.preventDefault();
    if (!ticketForm.subject.trim() || !ticketForm.description.trim()) {
      toast.error("Please fill in the Subject and Description.");
      return;
    }

    setUpdating(true);
    try {
      let attachmentUrl = "";
      if (ticketFile) {
        const fileRef = ref(storage, `employee_services/tickets/${Date.now()}_${ticketFile.name}`);
        const snap = await uploadBytes(fileRef, ticketFile);
        attachmentUrl = await getDownloadURL(snap.ref);
      }

      // Auto-assign to Employee's Team Lead
      let teamLeadId = userProfile?.leadId || userProfile?.reportingTo || userProfile?.teamLeadId || "";
      let teamLeadName = "Team Lead";

      if (!teamLeadId) {
        const foundLead = employees.find((emp) => emp.role === "lead" && emp.department === userDept);
        if (foundLead) {
          teamLeadId = foundLead.id;
          teamLeadName = foundLead.displayName || foundLead.name || "Team Lead";
        }
      } else {
        const leadObj = employees.find((emp) => emp.id === teamLeadId);
        if (leadObj) teamLeadName = leadObj.displayName || leadObj.name || "Team Lead";
      }

      const ticketNumber = `TKT-${Math.floor(1000 + Math.random() * 9000)}`;

      const newTicketData = {
        ticketNumber,
        subject: ticketForm.subject.trim(),
        category: ticketForm.category,
        priority: ticketForm.priority,
        description: ticketForm.description.trim(),
        attachmentUrl,
        status: "Open",
        escalationLevel: "team_lead",
        createdById: userId,
        createdByName: userName,
        createdByEmail: userProfile?.email || "",
        department: userDept,
        team: userTeam,
        teamLeadId,
        teamLeadName,
        assignedTo: teamLeadId || "team_lead_queue",
        assignedToName: teamLeadName,
        hrOwnerId: "",
        adminOwnerId: "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        replies: [],
        timeline: [
          {
            timestamp: new Date().toISOString(),
            actorId: userId,
            actorName: userName,
            actorRole: role,
            action: "Ticket Created",
            fromStatus: "-",
            toStatus: "Open",
            notes: `Support ticket submitted and assigned to ${teamLeadName}.`,
          },
        ],
      };

      await addDoc(collection(db, "employee_services_tickets"), newTicketData);

      if (teamLeadId) {
        await sendNotification({
          recipientId: teamLeadId,
          title: "New Ticket Assigned",
          message: `New support ticket #${ticketNumber} (${ticketForm.category}) from ${userName} requires your review.`,
          link: "/employee/services",
        });
      }

      await sendNotification({
        recipientId: userId,
        title: "Ticket Submitted",
        message: `Your support ticket #${ticketNumber} has been submitted and assigned to your Team Lead.`,
        link: "/employee/services",
      });

      toast.success(`Ticket #${ticketNumber} created successfully!`);
      setTicketForm(initialTicket);
      setTicketFile(null);
      setShowTicketForm(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit support ticket.");
    } finally {
      setUpdating(false);
    }
  };

  // ── Ticket Workflow Actions (Execute state transitions) ───────
  const handleExecuteAction = async (actionType) => {
    if (!selectedTicket) return;

    setUpdating(true);
    try {
      const ticketRef = doc(db, "employee_services_tickets", selectedTicket.id);
      let newStatus = selectedTicket.status;
      let newEscalation = selectedTicket.escalationLevel || "team_lead";
      let newAssignedTo = selectedTicket.assignedTo;
      let newAssignedName = selectedTicket.assignedToName;
      let actionLabel = "";
      let notificationMsg = "";
      let notifyRecipients = [];

      const currentTimeline = Array.isArray(selectedTicket.timeline) ? selectedTicket.timeline : [];

      if (actionType === "assign_self") {
        newStatus = "Assigned";
        newAssignedTo = userId;
        newAssignedName = userName;
        actionLabel = "Assigned to Self";
        notificationMsg = `Ticket #${selectedTicket.ticketNumber} was assigned to ${userName}.`;
        notifyRecipients = [selectedTicket.createdById];
      } else if (actionType === "under_review") {
        newStatus = "Under Review";
        actionLabel = "Under Review";
        notificationMsg = `Ticket #${selectedTicket.ticketNumber} is now under review by ${userName}.`;
        notifyRecipients = [selectedTicket.createdById];
      } else if (actionType === "in_progress") {
        newStatus = "In Progress";
        actionLabel = "In Progress";
        notificationMsg = `Work started on ticket #${selectedTicket.ticketNumber}.`;
        notifyRecipients = [selectedTicket.createdById];
      } else if (actionType === "request_info") {
        newStatus = "Waiting for Employee";
        actionLabel = "Information Requested";
        notificationMsg = `Additional information requested on ticket #${selectedTicket.ticketNumber}: "${actionNotes}"`;
        notifyRecipients = [selectedTicket.createdById];
      } else if (actionType === "escalate_hr") {
        newStatus = "Escalated to HR";
        newEscalation = "hr";
        newAssignedTo = "hr_team_queue";
        newAssignedName = "HR Operations Team";
        actionLabel = "Escalated to HR";
        notificationMsg = `Ticket #${selectedTicket.ticketNumber} has been escalated to HR for resolution.`;
        notifyRecipients = [selectedTicket.createdById, "hr"];
      } else if (actionType === "escalate_admin") {
        newStatus = "Waiting for Admin";
        newEscalation = "admin";
        newAssignedTo = "admin_queue";
        newAssignedName = "System Admin";
        actionLabel = "Escalated to Admin";
        notificationMsg = `Ticket #${selectedTicket.ticketNumber} escalated to Admin for system/configuration action.`;
        notifyRecipients = [selectedTicket.createdById, "admin"];
      } else if (actionType === "return_hr") {
        newStatus = "In Progress";
        newEscalation = "hr";
        newAssignedTo = "hr_team_queue";
        newAssignedName = "HR Operations Team";
        actionLabel = "Returned from Admin to HR";
        notificationMsg = `Administrative action for ticket #${selectedTicket.ticketNumber} completed. Returned to HR.`;
        notifyRecipients = [selectedTicket.createdById, "hr"];
      } else if (actionType === "resolve") {
        newStatus = "Resolved";
        actionLabel = "Resolved";
        notificationMsg = `Your support ticket #${selectedTicket.ticketNumber} has been resolved!`;
        notifyRecipients = [selectedTicket.createdById];
      } else if (actionType === "close") {
        newStatus = "Closed";
        actionLabel = "Closed by Employee";
        notificationMsg = `Ticket #${selectedTicket.ticketNumber} has been closed.`;
        notifyRecipients = [selectedTicket.assignedTo];
      } else if (actionType === "reject") {
        newStatus = "Rejected";
        actionLabel = "Rejected";
        notificationMsg = `Ticket #${selectedTicket.ticketNumber} was declined: "${actionNotes}"`;
        notifyRecipients = [selectedTicket.createdById];
      }

      const timelineEntry = {
        timestamp: new Date().toISOString(),
        actorId: userId,
        actorName: userName,
        actorRole: role,
        action: actionLabel,
        fromStatus: selectedTicket.status,
        toStatus: newStatus,
        notes: actionNotes || "",
      };

      const updatePayload = {
        status: newStatus,
        escalationLevel: newEscalation,
        assignedTo: newAssignedTo,
        assignedToName: newAssignedName,
        updatedAt: serverTimestamp(),
        timeline: [...currentTimeline, timelineEntry],
      };

      if (actionType === "resolve") {
        updatePayload.resolutionNotes = actionNotes || "Issue resolved successfully.";
        updatePayload.resolvedAt = serverTimestamp();
        updatePayload.resolvedBy = userName;
      }

      await updateDoc(ticketRef, updatePayload);

      for (const rec of notifyRecipients) {
        if (rec) {
          await sendNotification({
            recipientId: rec,
            title: `Ticket ${newStatus}`,
            message: notificationMsg,
            link: "/employee/services",
          });
        }
      }

      toast.success(`Action '${actionLabel}' recorded successfully.`);
      setActionNotes("");
      setSelectedActionModal(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update ticket action.");
    } finally {
      setUpdating(false);
    }
  };

  // ── Ticket Reply Thread ──────────────────────────────────────
  const handleAddReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedTicket) return;

    try {
      const ticketRef = doc(db, "employee_services_tickets", selectedTicket.id);
      const newReply = {
        id: Date.now().toString(),
        authorId: userId,
        authorName: userName,
        authorRole: role,
        text: replyText.trim(),
        timestamp: new Date().toISOString(),
      };

      const existingReplies = Array.isArray(selectedTicket.replies) ? selectedTicket.replies : [];
      await updateDoc(ticketRef, {
        replies: [...existingReplies, newReply],
        updatedAt: serverTimestamp(),
      });

      const notifyTarget = selectedTicket.createdById === userId ? selectedTicket.assignedTo : selectedTicket.createdById;
      if (notifyTarget) {
        await sendNotification({
          recipientId: notifyTarget,
          title: `New Reply on #${selectedTicket.ticketNumber}`,
          message: `${userName} replied: "${replyText.slice(0, 60)}..."`,
          link: "/employee/services",
        });
      }

      setReplyText("");
      toast.success("Reply posted.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to post reply.");
    }
  };

  // ── Announcements Actions (HR / Admin) ────────────────────────
  const handleSaveAnnouncement = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Please fill in Title and Description.");
      return;
    }
    setUpdating(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        department: form.department,
        priority: form.priority,
        audience: form.audience,
        expiryDate: form.expiryDate || "",
        createdById: userId,
        createdByName: userName,
        createdAt: serverTimestamp(),
        readBy: [],
      };
      await addDoc(collection(db, "employee_services_announcements"), payload);
      toast.success("Announcement published to " + form.audience);
      setForm(initialForm);
      setShowForm(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to publish announcement.");
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm("Are you sure you want to delete this announcement?")) return;
    try {
      await deleteDoc(doc(db, "employee_services_announcements", id));
      toast.success("Announcement removed.");
    } catch (e) {
      toast.error("Failed to delete announcement.");
    }
  };

  // ── Events Actions & RSVP ────────────────────────────────────
  const handleSaveEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.name.trim() || !eventForm.date) {
      toast.error("Please fill in Event Name and Date.");
      return;
    }
    setUpdating(true);
    try {
      const payload = {
        name: eventForm.name.trim(),
        date: eventForm.date,
        time: eventForm.time,
        venue: eventForm.venue,
        department: eventForm.department,
        targetAudience: eventForm.targetAudience,
        description: eventForm.description,
        registrationRequired: eventForm.registrationRequired,
        createdById: userId,
        createdByName: userName,
        createdAt: serverTimestamp(),
      };
      await addDoc(collection(db, "employee_services_events"), payload);
      toast.success("Event created successfully!");
      setEventForm(initialEvent);
      setShowEventForm(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to create event.");
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleRsvp = async (event, rsvpStatus) => {
    const existing = rsvps.find((r) => r.eventId === event.id && r.userId === userId);
    try {
      if (existing) {
        await updateDoc(doc(db, "employee_services_event_rsvps", existing.id), {
          status: rsvpStatus,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, "employee_services_event_rsvps"), {
          eventId: event.id,
          userId,
          userName,
          userEmail: userProfile?.email || "",
          userDept,
          status: rsvpStatus,
          createdAt: serverTimestamp(),
        });
      }
      toast.success(`RSVP updated: ${rsvpStatus}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update RSVP.");
    }
  };

  // ── Recognitions Actions ─────────────────────────────────────
  const handleSaveRecognition = async (e) => {
    e.preventDefault();
    if (!recognitionForm.employeeId || !recognitionForm.message.trim()) {
      toast.error("Please select an Employee and enter a message.");
      return;
    }
    setUpdating(true);
    try {
      const targetEmp = employees.find((emp) => emp.id === recognitionForm.employeeId);
      const targetName = targetEmp?.displayName || targetEmp?.name || "Employee";

      const status = isEmployee ? "pending_approval" : "published";

      const payload = {
        employeeId: recognitionForm.employeeId,
        employeeName: targetName,
        category: recognitionForm.category,
        message: recognitionForm.message.trim(),
        nominatedById: userId,
        nominatedByName: userName,
        nominatedByRole: role,
        department: targetEmp?.department || userDept,
        team: targetEmp?.team || userTeam,
        status,
        likes: [],
        awardedAt: serverTimestamp(),
      };

      await addDoc(collection(db, "employee_services_recognitions"), payload);

      if (status === "published") {
        await sendNotification({
          recipientId: recognitionForm.employeeId,
          title: "You Received a Recognition! 🏆",
          message: `${userName} recognized you for ${recognitionForm.category}: "${recognitionForm.message.slice(0, 50)}..."`,
          link: "/employee/services",
        });
        toast.success(`Recognition awarded to ${targetName}!`);
      } else {
        toast.success("Recognition submitted for HR/Lead approval.");
      }

      setRecognitionForm(initialRecognition);
      setShowRecognitionForm(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit recognition.");
    } finally {
      setUpdating(false);
    }
  };

  const handleLikeRecognition = async (rec) => {
    const likes = Array.isArray(rec.likes) ? rec.likes : [];
    const isLiked = likes.includes(userId);
    const updated = isLiked ? likes.filter((id) => id !== userId) : [...likes, userId];
    try {
      await updateDoc(doc(db, "employee_services_recognitions", rec.id), {
        likes: updated,
      });
    } catch (e) {
      console.warn(e);
    }
  };

  // ── Export Reports ───────────────────────────────────────────
  const exportReportsPDF = () => {
    const docObj = new jsPDF();
    docObj.setFontSize(16);
    docObj.text(`Employee Services Report - ${getUserRoleLabel(role)} View`, 14, 15);
    docObj.setFontSize(10);
    docObj.text(`Generated on: ${new Date().toLocaleDateString()} by ${userName}`, 14, 22);

    const tableData = roleFilteredTickets.map((t) => [
      t.ticketNumber || t.id.slice(0, 6),
      t.subject || "-",
      t.category || "-",
      t.priority || "-",
      t.status || "-",
      t.createdByName || "-",
      t.assignedToName || "-",
    ]);

    autoTable(docObj, {
      startY: 28,
      head: [["Ticket #", "Subject", "Category", "Priority", "Status", "Requester", "Assigned To"]],
      body: tableData,
    });
    docObj.save(`employee_services_report_${role}.pdf`);
  };

  const exportReportsExcel = () => {
    const data = roleFilteredTickets.map((t) => ({
      "Ticket Number": t.ticketNumber || t.id,
      Subject: t.subject,
      Category: t.category,
      Priority: t.priority,
      Status: t.status,
      "Requester Name": t.createdByName,
      "Requester Email": t.createdByEmail,
      Department: t.department,
      "Assigned To": t.assignedToName,
      "Escalation Level": t.escalationLevel || "team_lead",
      "Resolution Notes": t.resolutionNotes || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tickets");
    XLSX.writeFile(wb, `employee_services_report_${role}.xlsx`);
  };

  return (
    <div className="employee-services-wrapper">
      <style>{`
        .employee-services-wrapper {
          color: #0f172a;
          font-family: inherit;
        }
        .es-kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .es-kpi-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          padding: 1.1rem 1.25rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.03);
          transition: all 0.2s ease;
        }
        .es-kpi-card:hover {
          border-color: #10b981;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.08);
        }
        .es-kpi-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: #f0fdf4;
          border: 1.5px solid #bbf7d0;
          color: #15803d;
        }
        .es-kpi-val {
          font-size: 1.55rem;
          font-weight: 800;
          line-height: 1;
          color: #0f172a;
        }
        .es-kpi-label {
          font-size: 0.72rem;
          color: #64748b;
          margin-top: 4px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          font-weight: 700;
        }
        .es-nav-tabs {
          display: flex;
          gap: 0.5rem;
          border-bottom: 2px solid #e2e8f0;
          margin-bottom: 1.5rem;
          overflow-x: auto;
        }
        .es-tab-btn {
          padding: 0.65rem 1.25rem;
          font-size: 0.86rem;
          font-weight: 600;
          color: #64748b;
          border: none;
          background: transparent;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          border-bottom: 2.5px solid transparent;
          margin-bottom: -2px;
          transition: all 0.15s ease;
          white-space: nowrap;
        }
        .es-tab-btn.active {
          color: #15803d;
          font-weight: 700;
          border-bottom-color: #15803d;
          background: #f0fdf4;
          border-radius: 8px 8px 0 0;
        }
        .es-filter-bar {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
          margin-bottom: 1.25rem;
          align-items: center;
          background: #ffffff;
          padding: 0.85rem 1.1rem;
          border-radius: 14px;
          border: 1px solid #e2e8f0;
        }
        .es-search-box {
          flex: 1;
          min-width: 240px;
          position: relative;
        }
        .es-search-input {
          width: 100%;
          padding: 0.5rem 0.85rem 0.5rem 2.3rem;
          border: 1.5px solid #cbd5e1;
          border-radius: 10px;
          font-size: 0.84rem;
          background: #ffffff;
          color: #0f172a;
          outline: none;
        }
        .es-search-input:focus {
          border-color: #15803d;
        }
        .es-select {
          padding: 0.5rem 0.85rem;
          border-radius: 10px;
          border: 1.5px solid #cbd5e1;
          background: #ffffff;
          font-size: 0.82rem;
          font-weight: 600;
          color: #0f172a;
          outline: none;
        }
        .es-select:focus {
          border-color: #15803d;
        }
        .btn-green-primary {
          background: #1b4b31;
          color: #ffffff;
          border: none;
          border-radius: 9px;
          padding: 0.5rem 1rem;
          font-weight: 700;
          font-size: 0.82rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          transition: all 0.18s ease;
        }
        .btn-green-primary:hover {
          background: #143724;
        }
        .btn-outline-action {
          background: #ffffff;
          border: 1.5px solid #cbd5e1;
          border-radius: 9px;
          padding: 0.45rem 0.9rem;
          font-weight: 700;
          font-size: 0.8rem;
          color: #475569;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          transition: all 0.18s ease;
        }
        .btn-outline-action:hover {
          background: #f8fafc;
          border-color: #10b981;
          color: #15803d;
        }
        .es-table-wrap {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0,0,0,0.03);
        }
        .es-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.83rem;
        }
        .es-table th {
          background: #f8fafc;
          text-align: left;
          padding: 0.75rem 0.95rem;
          font-size: 0.74rem;
          font-weight: 700;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          border-bottom: 1.5px solid #e2e8f0;
          white-space: nowrap;
        }
        .es-table td {
          padding: 0.8rem 0.95rem;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: middle;
        }
        .es-table tbody tr:hover td {
          background: #f8fafc;
        }
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(3px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }
        .modal-content {
          background: #ffffff;
          border-radius: 16px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 50px rgba(0,0,0,0.2);
        }
        .timeline-step {
          position: relative;
          padding-left: 1.75rem;
          padding-bottom: 1.25rem;
          border-left: 2px solid #bbf7d0;
        }
        .timeline-step:last-child {
          border-left: 2px solid transparent;
          padding-bottom: 0;
        }
        .timeline-dot {
          position: absolute;
          left: -7px;
          top: 0;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #15803d;
          border: 2px solid #ffffff;
        }
      `}</style>

      {/* Header Banner */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "1rem",
          marginBottom: "1.25rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <LifeBuoy size={24} strokeWidth={1.8} color="#1b4b31" />
          <h1 style={{ fontSize: "1.5rem", fontWeight: 800, margin: 0, letterSpacing: "-0.02em", color: "#1b4b31" }}>
            {isEmployee
              ? "Employee Services Portal"
              : isLead
              ? "Team Lead Services Command Center"
              : isAdmin
              ? "Admin Services & Escalations Hub"
              : "Employee Services Command Center"}
          </h1>
        </div>

        <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
          <button className="btn-green-primary" onClick={() => setShowTicketForm(true)}>
            <PlusCircle size={15} strokeWidth={2} /> Raise Support Ticket
          </button>
          {canManageAnn && (
            <button className="btn-outline-action" onClick={() => setShowForm(true)}>
              <Megaphone size={15} strokeWidth={1.8} /> Publish Announcement
            </button>
          )}
          {canManageEvt && (
            <button className="btn-outline-action" onClick={() => setShowEventForm(true)}>
              <CalendarDays size={15} strokeWidth={1.8} /> Create Event
            </button>
          )}
          <button className="btn-outline-action" onClick={() => setShowRecognitionForm(true)}>
            <Sparkles size={15} strokeWidth={1.8} /> Give Recognition
          </button>
        </div>
      </div>

      {/* Top 4 KPI Metrics */}
      <div className="es-kpi-grid">
        {kpiData.map((k, i) => {
          const IconComponent = k.Icon;
          return (
            <div key={i} className="es-kpi-card">
              <div className="es-kpi-icon">
                <IconComponent className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div>
                <div className="es-kpi-val">{k.val}</div>
                <div className="es-kpi-label">{k.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation Tabs */}
      <div className="es-nav-tabs">
        {EMPLOYEE_SERVICE_SECTIONS.map((sec) => (
          <button
            key={sec.key}
            className={`es-tab-btn ${activeSection === sec.key ? "active" : ""}`}
            onClick={() => setActiveSection(sec.key)}
          >
            {sec.key === "overview" && <Layers size={16} strokeWidth={1.8} />}
            {sec.key === "helpdesk" && <ClipboardList size={16} strokeWidth={1.8} />}
            {sec.key === "announcements" && <Megaphone size={16} strokeWidth={1.8} />}
            {sec.key === "events" && <CalendarDays size={16} strokeWidth={1.8} />}
            {sec.key === "recognition" && <Award size={16} strokeWidth={1.8} />}
            {sec.key === "reports" && <FileText size={16} strokeWidth={1.8} />}
            {sec.key === "helpdesk" && isLead
              ? "Team Help Desk"
              : sec.key === "helpdesk" && isEmployee
              ? "My Tickets"
              : sec.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: OVERVIEW ──────────────────────────────────────── */}
      {activeSection === "overview" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "1.25rem" }}>
          {/* Active Tickets Widget */}
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "1.25rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: 0, color: "#1b4b31", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <ClipboardList size={18} strokeWidth={1.8} color="#1b4b31" />
                {isEmployee ? "My Active Tickets" : isLead ? "Pending Team Tickets" : "Recent Active Tickets"}
              </h3>
              <button
                className="btn-outline-action"
                style={{ padding: "3px 8px", fontSize: "0.72rem" }}
                onClick={() => setActiveSection("helpdesk")}
              >
                View All <ChevronRight size={14} />
              </button>
            </div>

            {roleFilteredTickets.slice(0, 4).length === 0 ? (
              <div style={{ textAlign: "center", padding: "2rem 0", color: "#64748b", fontSize: "0.84rem" }}>
                No active tickets currently.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                {roleFilteredTickets.slice(0, 4).map((t) => (
                  <div
                    key={t.id}
                    onClick={() => setSelectedTicket(t)}
                    style={{
                      padding: "0.75rem",
                      borderRadius: 10,
                      border: "1px solid #e2e8f0",
                      background: "#f8fafc",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0f172a" }}>
                        {t.ticketNumber || t.id.slice(0, 6)}: {t.subject}
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "#64748b", marginTop: 2 }}>
                        {t.category} · By {t.createdByName}
                      </div>
                    </div>
                    <span
                      style={{
                        ...getStatusBadgeStyle(t.status),
                        padding: "2px 8px",
                        borderRadius: 99,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Announcements Widget */}
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "1.25rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: 0, color: "#1b4b31", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Megaphone size={18} strokeWidth={1.8} color="#1b4b31" />
                Latest Announcements
              </h3>
              <button
                className="btn-outline-action"
                style={{ padding: "3px 8px", fontSize: "0.72rem" }}
                onClick={() => setActiveSection("announcements")}
              >
                View All <ChevronRight size={14} />
              </button>
            </div>

            {filteredAnnouncements.slice(0, 3).length === 0 ? (
              <div style={{ textAlign: "center", padding: "2rem 0", color: "#64748b", fontSize: "0.84rem" }}>
                No announcements published yet.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                {filteredAnnouncements.slice(0, 3).map((a) => (
                  <div key={a.id} style={{ padding: "0.75rem", borderRadius: 10, border: "1px solid #e2e8f0", background: "#f0fdf4" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "#15803d" }}>{a.title}</span>
                      <span style={{ fontSize: "0.7rem", color: "#64748b" }}>{a.department}</span>
                    </div>
                    <p style={{ margin: "4px 0 0", fontSize: "0.78rem", color: "#334155", lineHeight: 1.4 }}>
                      {a.description?.slice(0, 120)}...
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB 2: HELP DESK (TICKETS QUEUE) ────────────────────── */}
      {activeSection === "helpdesk" && (
        <div>
          {/* Filters Bar */}
          <div className="es-filter-bar">
            <div className="es-search-box">
              <Search size={16} strokeWidth={1.8} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
              <input
                type="text"
                className="es-search-input"
                placeholder="Search ticket #, subject, employee, category..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select className="es-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Statuses</option>
              {TICKET_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <select className="es-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="all">All Categories</option>
              {TICKET_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            <select className="es-select" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
              <option value="all">All Priorities</option>
              {TICKET_PRIORITIES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          {/* Tickets Table */}
          <div className="es-table-wrap">
            <table className="es-table">
              <thead>
                <tr>
                  <th>Ticket #</th>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Requester</th>
                  <th>Assigned To</th>
                  <th>Stage</th>
                  <th style={{ textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredTickets.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: "center", padding: "3rem", color: "#64748b" }}>
                      No tickets matching your current filter.
                    </td>
                  </tr>
                ) : (
                  filteredTickets.map((t) => (
                    <tr key={t.id}>
                      <td style={{ fontWeight: 800, color: "#1b4b31" }}>{t.ticketNumber || t.id.slice(0, 6)}</td>
                      <td style={{ fontWeight: 700, color: "#0f172a", maxWidth: 220 }}>{t.subject}</td>
                      <td>
                        <span style={{ fontSize: "0.75rem", background: "#f1f5f9", padding: "2px 7px", borderRadius: 6, fontWeight: 600 }}>
                          {t.category}
                        </span>
                      </td>
                      <td>
                        <span style={{ ...getPriorityBadgeStyle(t.priority), padding: "2px 7px", borderRadius: 6, fontSize: "0.72rem" }}>
                          {t.priority}
                        </span>
                      </td>
                      <td>
                        <span style={{ ...getStatusBadgeStyle(t.status), padding: "3px 8px", borderRadius: 99, fontSize: "0.72rem", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 3 }}>
                          {t.status}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{t.createdByName}</div>
                        <div style={{ fontSize: "0.7rem", color: "#64748b" }}>{t.department}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, color: "#334155" }}>{t.assignedToName || "Unassigned"}</div>
                      </td>
                      <td>
                        <span style={{ fontSize: "0.72rem", color: "#15803d", fontWeight: 700, textTransform: "capitalize" }}>
                          {t.escalationLevel === "admin" ? "Admin Level" : t.escalationLevel === "hr" ? "HR Level" : "Team Lead Level"}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <button
                          className="btn-outline-action"
                          style={{ padding: "4px 8px", fontSize: "0.75rem" }}
                          onClick={() => setSelectedTicket(t)}
                        >
                          <Eye size={13} /> View / Manage
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TAB 3: ANNOUNCEMENTS ─────────────────────────────────── */}
      {activeSection === "announcements" && (
        <div>
          <div className="es-filter-bar">
            <div className="es-search-box">
              <Search size={16} strokeWidth={1.8} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
              <input
                type="text"
                className="es-search-input"
                placeholder="Search announcements..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {canManageAnn && (
              <button className="btn-green-primary" onClick={() => setShowForm(true)}>
                <Plus size={15} /> New Announcement
              </button>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "1.25rem" }}>
            {filteredAnnouncements.map((a) => (
              <div
                key={a.id}
                style={{
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 14,
                  padding: "1.25rem",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                }}
              >
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem", marginBottom: "0.5rem" }}>
                    <h4 style={{ fontSize: "1.05rem", fontWeight: 800, margin: 0, color: "#1b4b31" }}>{a.title}</h4>
                    <span style={{ fontSize: "0.72rem", background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", padding: "2px 7px", borderRadius: 99, fontWeight: 700, whiteSpace: "nowrap" }}>
                      {a.audience || "Entire Organization"}
                    </span>
                  </div>
                  <p style={{ fontSize: "0.83rem", color: "#334155", lineHeight: 1.5, margin: "0 0 1rem" }}>
                    {a.description}
                  </p>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "0.75rem", borderTop: "1px solid #f1f5f9", fontSize: "0.75rem", color: "#64748b" }}>
                  <span>By {a.createdByName}</span>
                  {canManageAnn && (
                    <button
                      style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontWeight: 700, fontSize: "0.75rem" }}
                      onClick={() => handleDeleteAnnouncement(a.id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TAB 4: EVENTS & RSVPs ─────────────────────────────────── */}
      {activeSection === "events" && (
        <div>
          <div className="es-filter-bar">
            <div className="es-search-box">
              <Search size={16} strokeWidth={1.8} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
              <input
                type="text"
                className="es-search-input"
                placeholder="Search upcoming events..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              className={`es-tab-btn ${myEventsOnly ? "active" : ""}`}
              style={{ border: "1.5px solid #cbd5e1", borderRadius: 8, padding: "0.45rem 0.85rem" }}
              onClick={() => setMyEventsOnly(!myEventsOnly)}
            >
              <UserCheck size={14} /> My Events
            </button>
            {canManageEvt && (
              <button className="btn-green-primary" onClick={() => setShowEventForm(true)}>
                <Plus size={15} /> Create Event
              </button>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "1.25rem" }}>
            {filteredEvents.map((evt) => {
              const myRsvp = rsvps.find((r) => r.eventId === evt.id && r.userId === userId)?.status;
              const attendeesCount = rsvps.filter((r) => r.eventId === evt.id && r.status === "Going").length;

              return (
                <div
                  key={evt.id}
                  style={{
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 14,
                    padding: "1.25rem",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                      <h4 style={{ fontSize: "1.05rem", fontWeight: 800, margin: 0, color: "#1b4b31" }}>{evt.name}</h4>
                      <span style={{ fontSize: "0.72rem", background: "#f0fdf4", color: "#15803d", padding: "2px 7px", borderRadius: 6, fontWeight: 700 }}>
                        {evt.department}
                      </span>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", fontSize: "0.78rem", color: "#475569", marginBottom: "0.75rem" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <CalendarDays size={14} color="#15803d" /> {evt.date} at {evt.time}
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <MapPin size={14} color="#15803d" /> {evt.venue}
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <Users size={14} color="#15803d" /> {attendeesCount} Attending
                      </span>
                    </div>

                    <p style={{ fontSize: "0.82rem", color: "#334155", lineHeight: 1.4, margin: "0 0 1rem" }}>
                      {evt.description}
                    </p>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "0.75rem", borderTop: "1px solid #f1f5f9" }}>
                    <div style={{ display: "flex", gap: "0.4rem" }}>
                      <button
                        style={{
                          padding: "4px 9px",
                          borderRadius: 7,
                          fontSize: "0.74rem",
                          fontWeight: 700,
                          cursor: "pointer",
                          background: myRsvp === "Going" ? "#15803d" : "#ffffff",
                          color: myRsvp === "Going" ? "#ffffff" : "#15803d",
                          border: "1.5px solid #15803d",
                        }}
                        onClick={() => handleToggleRsvp(evt, "Going")}
                      >
                        {myRsvp === "Going" ? "✓ Going" : "RSVP Going"}
                      </button>
                      <button
                        style={{
                          padding: "4px 8px",
                          borderRadius: 7,
                          fontSize: "0.74rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          background: myRsvp === "Not Going" ? "#ef4444" : "#ffffff",
                          color: myRsvp === "Not Going" ? "#ffffff" : "#64748b",
                          border: "1px solid #cbd5e1",
                        }}
                        onClick={() => handleToggleRsvp(evt, "Not Going")}
                      >
                        Not Going
                      </button>
                    </div>

                    {canManageEvt && (
                      <button
                        className="btn-outline-action"
                        style={{ padding: "4px 8px", fontSize: "0.74rem" }}
                        onClick={() => setShowEventAttendeesModal(evt)}
                      >
                        Attendees ({attendeesCount})
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── TAB 5: RECOGNITION (WALL OF FAME) ─────────────────────── */}
      {activeSection === "recognition" && (
        <div>
          <div className="es-filter-bar">
            <h3 style={{ fontSize: "1rem", fontWeight: 800, margin: 0, color: "#1b4b31", display: "flex", alignItems: "center", gap: 6 }}>
              <Award size={18} color="#1b4b31" /> Employee Wall of Fame & Recognition
            </h3>
            <button className="btn-green-primary" onClick={() => setShowRecognitionForm(true)}>
              <Sparkles size={15} /> Recognize Employee
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "1.25rem" }}>
            {filteredRecognitions.map((r) => {
              const likes = Array.isArray(r.likes) ? r.likes : [];
              const isLiked = likes.includes(userId);

              return (
                <div
                  key={r.id}
                  style={{
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 14,
                    padding: "1.25rem",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.75rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                        <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#1b4b31", color: "#ffffff", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {r.employeeName?.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: "0.95rem", fontWeight: 800, color: "#0f172a" }}>{r.employeeName}</div>
                          <div style={{ fontSize: "0.72rem", color: "#64748b" }}>{r.department}</div>
                        </div>
                      </div>
                      <span style={{ fontSize: "0.72rem", background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", padding: "2px 8px", borderRadius: 99, fontWeight: 700 }}>
                        {r.category}
                      </span>
                    </div>

                    <p style={{ fontSize: "0.84rem", color: "#334155", lineHeight: 1.5, background: "#f8fafc", padding: "0.75rem", borderRadius: 8, fontStyle: "italic" }}>
                      "{r.message}"
                    </p>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "0.75rem", borderTop: "1px solid #f1f5f9", fontSize: "0.74rem", color: "#64748b" }}>
                    <span>Awarded by <strong>{r.nominatedByName}</strong></span>
                    <button
                      style={{
                        background: isLiked ? "#f0fdf4" : "transparent",
                        border: "1px solid #cbd5e1",
                        borderRadius: 99,
                        padding: "3px 9px",
                        fontSize: "0.72rem",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        color: isLiked ? "#15803d" : "#64748b",
                        fontWeight: 700,
                      }}
                      onClick={() => handleLikeRecognition(r)}
                    >
                      <ThumbsUp size={12} /> {likes.length} Applaud
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── TAB 6: REPORTS & ANALYTICS ───────────────────────────── */}
      {activeSection === "reports" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          <div className="es-filter-bar">
            <div style={{ fontWeight: 800, color: "#1b4b31", fontSize: "0.95rem" }}>
              {getUserRoleLabel(role)} Service Analytics & Reports
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button className="btn-outline-action" onClick={exportReportsPDF}>
                <Printer size={14} /> PDF Report
              </button>
              <button className="btn-outline-action" onClick={exportReportsExcel}>
                <Download size={14} /> Excel Export
              </button>
            </div>
          </div>

          {/* Analytics Summary Cards */}
          <div className="es-kpi-grid">
            <div className="es-kpi-card">
              <div className="es-kpi-icon"><ClipboardList size={20} /></div>
              <div>
                <div className="es-kpi-val">{roleFilteredTickets.length}</div>
                <div className="es-kpi-label">Total Tickets Processed</div>
              </div>
            </div>
            <div className="es-kpi-card">
              <div className="es-kpi-icon"><CheckCircle2 size={20} /></div>
              <div>
                <div className="es-kpi-val">{roleFilteredTickets.filter((t) => t.status === "Resolved" || t.status === "Closed").length}</div>
                <div className="es-kpi-label">Resolved & Closed</div>
              </div>
            </div>
            <div className="es-kpi-card">
              <div className="es-kpi-icon"><Clock size={20} /></div>
              <div>
                <div className="es-kpi-val">
                  {roleFilteredTickets.filter((t) => t.status === "Escalated to HR" || t.status === "Waiting for Admin").length}
                </div>
                <div className="es-kpi-label">Active Escalations</div>
              </div>
            </div>
            <div className="es-kpi-card">
              <div className="es-kpi-icon"><Award size={20} /></div>
              <div>
                <div className="es-kpi-val">{recognitions.length}</div>
                <div className="es-kpi-label">Recognitions Recorded</div>
              </div>
            </div>
          </div>

          {/* Category Breakdown Table */}
          <div className="es-table-wrap">
            <div style={{ padding: "0.85rem 1.1rem", borderBottom: "1px solid #e2e8f0", fontWeight: 800, color: "#1b4b31" }}>
              Tickets Breakdown by Category
            </div>
            <table className="es-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Total Requests</th>
                  <th>Open / Under Review</th>
                  <th>Resolved</th>
                  <th>Resolution Rate</th>
                </tr>
              </thead>
              <tbody>
                {TICKET_CATEGORIES.map((cat) => {
                  const inCat = roleFilteredTickets.filter((t) => t.category === cat);
                  const resolvedInCat = inCat.filter((t) => t.status === "Resolved" || t.status === "Closed").length;
                  const openInCat = inCat.length - resolvedInCat;
                  const rate = inCat.length ? Math.round((resolvedInCat / inCat.length) * 100) : 0;

                  return (
                    <tr key={cat}>
                      <td style={{ fontWeight: 700, color: "#0f172a" }}>{cat}</td>
                      <td>{inCat.length}</td>
                      <td>{openInCat}</td>
                      <td style={{ color: "#15803d", fontWeight: 700 }}>{resolvedInCat}</td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ flex: 1, height: 6, background: "#e2e8f0", borderRadius: 99 }}>
                            <div style={{ width: `${rate}%`, height: "100%", background: "#15803d", borderRadius: 99 }} />
                          </div>
                          <span style={{ fontSize: "0.75rem", fontWeight: 700 }}>{rate}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── MODAL: RAISE SUPPORT TICKET ─────────────────────────── */}
      {showTicketForm && (
        <div className="modal-overlay" onClick={() => setShowTicketForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "1.25rem 1.5rem", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <PlusCircle size={20} color="#1b4b31" />
                <h3 style={{ fontSize: "1.15rem", fontWeight: 800, margin: 0, color: "#1b4b31" }}>Raise Support Ticket</h3>
              </div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }} onClick={() => setShowTicketForm(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleRaiseTicket} style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Subject *</label>
                <input
                  type="text"
                  required
                  style={{ width: "100%", padding: "0.55rem 0.85rem", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: "0.85rem", outline: "none", boxSizing: "border-box" }}
                  placeholder="e.g. Discrepancy in July Payslip"
                  value={ticketForm.subject}
                  onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Category</label>
                  <select
                    style={{ width: "100%", padding: "0.55rem 0.85rem", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: "0.85rem", outline: "none" }}
                    value={ticketForm.category}
                    onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}
                  >
                    {TICKET_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Priority</label>
                  <select
                    style={{ width: "100%", padding: "0.55rem 0.85rem", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: "0.85rem", outline: "none" }}
                    value={ticketForm.priority}
                    onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value })}
                  >
                    {TICKET_PRIORITIES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Description *</label>
                <textarea
                  rows={4}
                  required
                  style={{ width: "100%", padding: "0.55rem 0.85rem", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: "0.85rem", outline: "none", boxSizing: "border-box" }}
                  placeholder="Provide complete details about your issue..."
                  value={ticketForm.description}
                  onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                />
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Attachment (Optional)</label>
                <input
                  type="file"
                  style={{ fontSize: "0.82rem", color: "#64748b" }}
                  onChange={(e) => setTicketFile(e.target.files[0] || null)}
                />
              </div>

              <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 9, padding: "0.65rem 0.85rem", fontSize: "0.75rem", color: "#15803d" }}>
                ✓ This ticket will automatically route to your <strong>Team Lead</strong> for initial review and assignment.
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
                <button type="button" className="btn-outline-action" onClick={() => setShowTicketForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-green-primary" disabled={updating}>
                  {updating ? "Submitting..." : "Submit Ticket"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL: TICKET DETAILS & WORKFLOW DRAWER ──────────────── */}
      {selectedTicket && (
        <div className="modal-overlay" onClick={() => setSelectedTicket(null)}>
          <div className="modal-content" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: "1.25rem 1.5rem", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ fontSize: "1.15rem", fontWeight: 800, color: "#1b4b31" }}>
                    {selectedTicket.ticketNumber || selectedTicket.id.slice(0, 6)}
                  </span>
                  <span style={{ ...getStatusBadgeStyle(selectedTicket.status), padding: "2px 8px", borderRadius: 99, fontSize: "0.72rem", fontWeight: 700 }}>
                    {selectedTicket.status}
                  </span>
                </div>
                <h4 style={{ margin: "4px 0 0", fontSize: "0.95rem", fontWeight: 700, color: "#0f172a" }}>
                  {selectedTicket.subject}
                </h4>
              </div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }} onClick={() => setSelectedTicket(null)}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>
              {/* Metadata Grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.75rem", background: "#f8fafc", padding: "0.85rem", borderRadius: 10, border: "1px solid #e2e8f0", fontSize: "0.76rem" }}>
                <div>
                  <span style={{ color: "#64748b", display: "block" }}>Category</span>
                  <strong>{selectedTicket.category}</strong>
                </div>
                <div>
                  <span style={{ color: "#64748b", display: "block" }}>Priority</span>
                  <strong>{selectedTicket.priority}</strong>
                </div>
                <div>
                  <span style={{ color: "#64748b", display: "block" }}>Requester</span>
                  <strong>{selectedTicket.createdByName}</strong>
                </div>
                <div>
                  <span style={{ color: "#64748b", display: "block" }}>Department</span>
                  <strong>{selectedTicket.department}</strong>
                </div>
                <div>
                  <span style={{ color: "#64748b", display: "block" }}>Current Assignee</span>
                  <strong>{selectedTicket.assignedToName || "Unassigned"}</strong>
                </div>
              </div>

              {/* Description & Attachment */}
              <div>
                <label style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Description</label>
                <p style={{ margin: "4px 0 0", fontSize: "0.84rem", color: "#334155", lineHeight: 1.5, background: "#ffffff", padding: "0.75rem", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                  {selectedTicket.description}
                </p>
                {selectedTicket.attachmentUrl && (
                  <div style={{ marginTop: "0.5rem" }}>
                    <a href={selectedTicket.attachmentUrl} target="_blank" rel="noreferrer" className="btn-outline-action" style={{ fontSize: "0.74rem", padding: "3px 8px" }}>
                      <Download size={13} /> View Attached Document
                    </a>
                  </div>
                )}
              </div>

              {/* Resolution Notes if Resolved */}
              {selectedTicket.resolutionNotes && (
                <div style={{ background: "#f0fdf4", border: "1.5px solid #bbf7d0", padding: "0.85rem", borderRadius: 10 }}>
                  <div style={{ fontSize: "0.75rem", fontWeight: 800, color: "#15803d", textTransform: "uppercase", marginBottom: 3 }}>
                    ✓ Resolution Summary (By {selectedTicket.resolvedBy || "Support"})
                  </div>
                  <div style={{ fontSize: "0.83rem", color: "#166534" }}>{selectedTicket.resolutionNotes}</div>
                </div>
              )}

              {/* Workflow Actions Control Panel (Role-based) */}
              <div style={{ background: "#ffffff", border: "1.5px solid #bbf7d0", borderRadius: 12, padding: "1rem" }}>
                <div style={{ fontSize: "0.75rem", fontWeight: 800, color: "#1b4b31", textTransform: "uppercase", marginBottom: "0.65rem", display: "flex", alignItems: "center", gap: 5 }}>
                  <ShieldCheck size={16} /> Available Actions ({getUserRoleLabel(role)})
                </div>

                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  {/* Employee Action: Confirm Close */}
                  {isEmployee && selectedTicket.status === "Resolved" && (
                    <button className="btn-green-primary" onClick={() => handleExecuteAction("close")}>
                      <Check size={14} /> Confirm & Close Ticket
                    </button>
                  )}

                  {/* Team Lead Actions */}
                  {isLead && (
                    <>
                      {selectedTicket.status === "Open" && (
                        <button className="btn-outline-action" onClick={() => handleExecuteAction("assign_self")}>
                          <UserCheck size={14} /> Assign to Myself
                        </button>
                      )}
                      {selectedTicket.status !== "In Progress" && selectedTicket.status !== "Resolved" && selectedTicket.status !== "Closed" && (
                        <button className="btn-outline-action" onClick={() => handleExecuteAction("in_progress")}>
                          Start Work
                        </button>
                      )}
                      <button className="btn-outline-action" onClick={() => setSelectedActionModal("request_info")}>
                        <HelpCircle size={14} /> Request Info
                      </button>
                      {canResolveTicket(role, selectedTicket) && (
                        <button className="btn-green-primary" onClick={() => setSelectedActionModal("resolve")}>
                          <Check size={14} /> Resolve Ticket
                        </button>
                      )}
                      {canEscalateToHR(role) && selectedTicket.status !== "Escalated to HR" && selectedTicket.status !== "Waiting for Admin" && (
                        <button className="btn-outline-action" style={{ borderColor: "#f59e0b", color: "#b45309" }} onClick={() => setSelectedActionModal("escalate_hr")}>
                          <CornerUpRight size={14} /> Escalate to HR
                        </button>
                      )}
                      <button className="btn-outline-action" style={{ borderColor: "#fca5a5", color: "#dc2626" }} onClick={() => setSelectedActionModal("reject")}>
                        <X size={14} /> Decline / Reject
                      </button>
                    </>
                  )}

                  {/* HR Actions */}
                  {isHR && (
                    <>
                      {selectedTicket.status !== "In Progress" && selectedTicket.status !== "Resolved" && selectedTicket.status !== "Closed" && (
                        <button className="btn-outline-action" onClick={() => handleExecuteAction("in_progress")}>
                          Set In Progress
                        </button>
                      )}
                      <button className="btn-outline-action" onClick={() => setSelectedActionModal("request_info")}>
                        <HelpCircle size={14} /> Request Info
                      </button>
                      <button className="btn-green-primary" onClick={() => setSelectedActionModal("resolve")}>
                        <Check size={14} /> Resolve Ticket
                      </button>
                      {canEscalateToAdmin(role) && selectedTicket.status !== "Waiting for Admin" && (
                        <button className="btn-outline-action" style={{ borderColor: "#f59e0b", color: "#b45309" }} onClick={() => setSelectedActionModal("escalate_admin")}>
                          <ShieldCheck size={14} /> Escalate to Admin
                        </button>
                      )}
                      <button className="btn-outline-action" style={{ borderColor: "#fca5a5", color: "#dc2626" }} onClick={() => setSelectedActionModal("reject")}>
                        <X size={14} /> Reject
                      </button>
                    </>
                  )}

                  {/* Admin Actions */}
                  {isAdmin && (
                    <>
                      <button className="btn-green-primary" onClick={() => setSelectedActionModal("resolve")}>
                        <Check size={14} /> Direct Resolve
                      </button>
                      {canReturnToHR(role) && (
                        <button className="btn-outline-action" style={{ borderColor: "#10b981", color: "#15803d" }} onClick={() => setSelectedActionModal("return_hr")}>
                          <CornerUpRight size={14} /> Complete & Return to HR
                        </button>
                      )}
                      <button className="btn-outline-action" style={{ borderColor: "#fca5a5", color: "#dc2626" }} onClick={() => setSelectedActionModal("reject")}>
                        <X size={14} /> Reject
                      </button>
                    </>
                  )}
                </div>

                {/* Sub-action prompt dialog */}
                {selectedActionModal && (
                  <div style={{ marginTop: "0.85rem", paddingTop: "0.85rem", borderTop: "1px dashed #cbd5e1" }}>
                    <label style={{ fontSize: "0.76rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>
                      {selectedActionModal === "resolve"
                        ? "Resolution Notes (Shared with Requester) *"
                        : selectedActionModal === "reject"
                        ? "Reason for Rejection *"
                        : selectedActionModal === "return_hr"
                        ? "Administrative Action Notes (Returned to HR) *"
                        : selectedActionModal === "request_info"
                        ? "Specify Information Required from Employee *"
                        : "Escalation Reason / Notes *"}
                    </label>
                    <textarea
                      rows={2}
                      style={{ width: "100%", padding: "0.5rem", borderRadius: 8, border: "1.5px solid #cbd5e1", fontSize: "0.82rem", boxSizing: "border-box" }}
                      placeholder="Enter remarks..."
                      value={actionNotes}
                      onChange={(e) => setActionNotes(e.target.value)}
                    />
                    <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
                      <button className="btn-green-primary" onClick={() => handleExecuteAction(selectedActionModal)}>
                        Confirm Action
                      </button>
                      <button className="btn-outline-action" onClick={() => setSelectedActionModal(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Complete Audit Trail Timeline */}
              <div>
                <label style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: "0.75rem" }}>
                  Complete Activity Timeline
                </label>
                <div style={{ paddingLeft: "0.5rem" }}>
                  {Array.isArray(selectedTicket.timeline) && selectedTicket.timeline.length > 0 ? (
                    selectedTicket.timeline.map((entry, idx) => (
                      <div key={idx} className="timeline-step">
                        <div className="timeline-dot" />
                        <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#0f172a" }}>
                          {entry.action} — <span style={{ color: "#15803d" }}>{entry.toStatus}</span>
                        </div>
                        <div style={{ fontSize: "0.72rem", color: "#64748b", marginTop: 2 }}>
                          By <strong>{entry.actorName}</strong> ({getUserRoleLabel(entry.actorRole)}) · {new Date(entry.timestamp).toLocaleString()}
                        </div>
                        {entry.notes && (
                          <div style={{ fontSize: "0.76rem", color: "#334155", fontStyle: "italic", marginTop: 4, background: "#f8fafc", padding: "4px 8px", borderRadius: 6 }}>
                            "{entry.notes}"
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: "0.8rem", color: "#64748b" }}>Timeline initiated at ticket creation.</div>
                  )}
                </div>
              </div>

              {/* Discussion & Replies Thread */}
              <div>
                <label style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: "0.5rem" }}>
                  Conversation Thread
                </label>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxHeight: 200, overflowY: "auto", marginBottom: "0.75rem" }}>
                  {Array.isArray(selectedTicket.replies) && selectedTicket.replies.length > 0 ? (
                    selectedTicket.replies.map((rep) => (
                      <div
                        key={rep.id}
                        style={{
                          background: rep.authorId === userId ? "#f0fdf4" : "#f8fafc",
                          border: `1px solid ${rep.authorId === userId ? "#bbf7d0" : "#e2e8f0"}`,
                          borderRadius: 8,
                          padding: "0.6rem 0.75rem",
                          alignSelf: rep.authorId === userId ? "flex-end" : "flex-start",
                          maxWidth: "85%",
                        }}
                      >
                        <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#15803d" }}>
                          {rep.authorName} ({getUserRoleLabel(rep.authorRole)})
                        </div>
                        <div style={{ fontSize: "0.82rem", color: "#0f172a", marginTop: 2 }}>{rep.text}</div>
                        <div style={{ fontSize: "0.68rem", color: "#94a3b8", marginTop: 2 }}>
                          {new Date(rep.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: "0.78rem", color: "#94a3b8", fontStyle: "italic" }}>No comments yet.</div>
                  )}
                </div>

                <form onSubmit={handleAddReply} style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    type="text"
                    style={{ flex: 1, padding: "0.5rem 0.85rem", borderRadius: 8, border: "1.5px solid #cbd5e1", fontSize: "0.82rem", outline: "none" }}
                    placeholder="Type a reply or question..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />
                  <button type="submit" className="btn-green-primary" style={{ padding: "0.5rem 0.9rem" }}>
                    <Send size={13} /> Reply
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: PUBLISH ANNOUNCEMENT ──────────────────────────── */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "1.25rem 1.5rem", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Megaphone size={20} color="#1b4b31" />
                <h3 style={{ fontSize: "1.15rem", fontWeight: 800, margin: 0, color: "#1b4b31" }}>Publish Announcement</h3>
              </div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }} onClick={() => setShowForm(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveAnnouncement} style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Title *</label>
                <input
                  type="text"
                  required
                  style={{ width: "100%", padding: "0.55rem 0.85rem", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: "0.85rem", boxSizing: "border-box" }}
                  placeholder="e.g. Annual Company Offsite 2026"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Target Audience</label>
                  <select
                    style={{ width: "100%", padding: "0.55rem 0.85rem", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: "0.85rem" }}
                    value={form.audience}
                    onChange={(e) => setForm({ ...form, audience: e.target.value })}
                  >
                    {ANNOUNCEMENT_AUDIENCES.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Department Scope</label>
                  <select
                    style={{ width: "100%", padding: "0.55rem 0.85rem", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: "0.85rem" }}
                    value={form.department}
                    onChange={(e) => setForm({ ...form, department: e.target.value })}
                  >
                    <option value="General">General / All</option>
                    <option value="Engineering">Engineering</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Sales">Sales</option>
                    <option value="HR">HR</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Message Content *</label>
                <textarea
                  rows={4}
                  required
                  style={{ width: "100%", padding: "0.55rem 0.85rem", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: "0.85rem", boxSizing: "border-box" }}
                  placeholder="Enter full announcement details..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                <button type="button" className="btn-outline-action" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-green-primary" disabled={updating}>
                  {updating ? "Publishing..." : "Publish Announcement"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL: CREATE EVENT ──────────────────────────────────── */}
      {showEventForm && (
        <div className="modal-overlay" onClick={() => setShowEventForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "1.25rem 1.5rem", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <CalendarDays size={20} color="#1b4b31" />
                <h3 style={{ fontSize: "1.15rem", fontWeight: 800, margin: 0, color: "#1b4b31" }}>Create Organization Event</h3>
              </div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }} onClick={() => setShowEventForm(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveEvent} style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Event Name *</label>
                <input
                  type="text"
                  required
                  style={{ width: "100%", padding: "0.55rem 0.85rem", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: "0.85rem", boxSizing: "border-box" }}
                  placeholder="e.g. Q3 Town Hall & Recognition"
                  value={eventForm.name}
                  onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Date *</label>
                  <input
                    type="date"
                    required
                    style={{ width: "100%", padding: "0.55rem 0.85rem", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: "0.85rem", boxSizing: "border-box" }}
                    value={eventForm.date}
                    onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Time</label>
                  <input
                    type="time"
                    style={{ width: "100%", padding: "0.55rem 0.85rem", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: "0.85rem", boxSizing: "border-box" }}
                    value={eventForm.time}
                    onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Location / Venue</label>
                <input
                  type="text"
                  style={{ width: "100%", padding: "0.55rem 0.85rem", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: "0.85rem", boxSizing: "border-box" }}
                  placeholder="e.g. Auditorium / Zoom Link"
                  value={eventForm.venue}
                  onChange={(e) => setEventForm({ ...eventForm, venue: e.target.value })}
                />
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Description</label>
                <textarea
                  rows={3}
                  style={{ width: "100%", padding: "0.55rem 0.85rem", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: "0.85rem", boxSizing: "border-box" }}
                  placeholder="Event agenda and details..."
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                <button type="button" className="btn-outline-action" onClick={() => setShowEventForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-green-primary" disabled={updating}>
                  {updating ? "Saving..." : "Create Event"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL: GIVE RECOGNITION ──────────────────────────────── */}
      {showRecognitionForm && (
        <div className="modal-overlay" onClick={() => setShowRecognitionForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "1.25rem 1.5rem", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Sparkles size={20} color="#1b4b31" />
                <h3 style={{ fontSize: "1.15rem", fontWeight: 800, margin: 0, color: "#1b4b31" }}>Recognize a Colleague</h3>
              </div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }} onClick={() => setShowRecognitionForm(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveRecognition} style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Select Employee *</label>
                <select
                  required
                  style={{ width: "100%", padding: "0.55rem 0.85rem", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: "0.85rem" }}
                  value={recognitionForm.employeeId}
                  onChange={(e) => setRecognitionForm({ ...recognitionForm, employeeId: e.target.value })}
                >
                  <option value="">-- Choose Employee --</option>
                  {employees
                    .filter((emp) => emp.id !== userId)
                    .map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.displayName || emp.name} ({emp.department || "General"})
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Category</label>
                <select
                  style={{ width: "100%", padding: "0.55rem 0.85rem", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: "0.85rem" }}
                  value={recognitionForm.category}
                  onChange={(e) => setRecognitionForm({ ...recognitionForm, category: e.target.value })}
                >
                  {RECOGNITION_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: 4 }}>Recognition Message *</label>
                <textarea
                  rows={4}
                  required
                  style={{ width: "100%", padding: "0.55rem 0.85rem", borderRadius: 9, border: "1.5px solid #cbd5e1", fontSize: "0.85rem", boxSizing: "border-box" }}
                  placeholder="Share what makes their contribution outstanding..."
                  value={recognitionForm.message}
                  onChange={(e) => setRecognitionForm({ ...recognitionForm, message: e.target.value })}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                <button type="button" className="btn-outline-action" onClick={() => setShowRecognitionForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-green-primary" disabled={updating}>
                  {updating ? "Submitting..." : "Award Recognition"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL: EVENT ATTENDEES LIST ─────────────────────────── */}
      {showEventAttendeesModal && (
        <div className="modal-overlay" onClick={() => setShowEventAttendeesModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "1.25rem 1.5rem", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h3 style={{ fontSize: "1.15rem", fontWeight: 800, margin: 0, color: "#1b4b31" }}>
                  Attendees: {showEventAttendeesModal.name}
                </h3>
                <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: 2 }}>
                  {showEventAttendeesModal.date} at {showEventAttendeesModal.time}
                </div>
              </div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }} onClick={() => setShowEventAttendeesModal(null)}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: "1.5rem" }}>
              <div style={{ maxHeight: 320, overflowY: "auto" }}>
                {rsvps.filter((r) => r.eventId === showEventAttendeesModal.id).length === 0 ? (
                  <div style={{ textAlign: "center", padding: "2rem", color: "#64748b", fontSize: "0.84rem" }}>
                    No RSVPs received yet.
                  </div>
                ) : (
                  <table className="es-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Department</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rsvps
                        .filter((r) => r.eventId === showEventAttendeesModal.id)
                        .map((r) => (
                          <tr key={r.id}>
                            <td style={{ fontWeight: 700 }}>{r.userName}</td>
                            <td>{r.userDept}</td>
                            <td>
                              <span
                                style={{
                                  fontSize: "0.72rem",
                                  fontWeight: 700,
                                  padding: "2px 7px",
                                  borderRadius: 6,
                                  background: r.status === "Going" ? "#f0fdf4" : "#fef2f2",
                                  color: r.status === "Going" ? "#15803d" : "#dc2626",
                                }}
                              >
                                {r.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
